import React, { useState, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../../../context/AuthContext';
import { 
  getSafetyCategories, 
  getRiskCauses, 
  getSafetyProcessMasters, 
  getSafetyMasterMappings,
  saveSafetyMasterMappings
} from '../../../api/safetyAssessApi';
import SafetyMatrixModal from '../shared/SafetyMatrixModal';

function SafetyMasterHazardMap({ comCode = '' }) {
  const { token } = useContext(AuthContext);

  const [categories, setCategories] = useState([]);
  const [causes, setCauses] = useState([]);
  const [processes, setProcesses] = useState([]);
  
  const [selectedProcessId, setSelectedProcessId] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [tempMappings, setTempMappings] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedCatId, setExpandedCatId] = useState(null);
  const [showMatrixModal, setShowMatrixModal] = useState(false);

  useEffect(() => {
    if (token && comCode) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, comCode]);

  const fetchData = async () => {
    try {
      const [catData, causeData, procData, mapData] = await Promise.all([
        getSafetyCategories(token, comCode),
        getRiskCauses('', token, comCode), // categoryId 없이 전체 조회
        getSafetyProcessMasters(token, comCode),
        getSafetyMasterMappings(token, comCode)
      ]);
      const colorMap = {
        1: { main: '#7c3aed', sub: '#a78bfa' }, // 기계 (진보라)
        2: { main: '#f59e0b', sub: '#fbbf24' }, // 전기 (주황)
        3: { main: '#059669', sub: '#34d399' }, // 화학 (초록)
        4: { main: '#dc2626', sub: '#f87171' }, // 생물 (빨강)
        5: { main: '#2563eb', sub: '#60a5fa' }, // 작업특성 (파랑)
        6: { main: '#0891b2', sub: '#22d3ee' }, // 작업환경 (시안)
        7: { main: '#db2777', sub: '#f472b6' }, // 예비1 (핑크)
        8: { main: '#65a30d', sub: '#a3e635' }, // 예비2 (라임)
        9: { main: '#475569', sub: '#94a3b8' }, // 예비3 (슬레이트)
        10: { main: '#9333ea', sub: '#c084fc' }, // 예비4 (퍼플)
      };
      const enrichedCategories = (catData || []).map((c, i) => {
        const cColors = colorMap[c.id] || colorMap[(i % 10) + 1];
        return { ...c, color: cColors.main, subColor: cColors.sub };
      });
      setCategories(enrichedCategories);
      setCauses(causeData || []);
      setProcesses(procData || []);
      setMappings(mapData || []);
      if (procData && procData.length > 0) {
        setSelectedProcessId(procData[0].id);
      }
    } catch (error) {
      console.error('마스터 맵핑 데이터 로드 실패:', error);
    }
  };

  const selectedProcess = processes.find(p => p.id === selectedProcessId);

  const selectedCauses = mappings
    .filter(m => m.processId === selectedProcessId)
    .map(m => {
      const cause = causes.find(c => c.id === m.causeId);
      const cat = categories.find(ct => ct.id === cause?.categoryId);
      return { ...cause, categoryName: cat?.name, categoryColor: cat?.color };
    })
    .filter(Boolean);

  const handleOpenAddModal = () => {
    setTempMappings([...mappings]); // 현재 상태 복사
    setExpandedCatId(categories.length > 0 ? categories[0].id : null); // 첫번째 대분류 펼침
    setShowAddModal(true);
  };

  const handleTempAddCause = (causeId) => {
    const exists = tempMappings.some(m => m.processId === selectedProcessId && m.causeId === causeId);
    if (!exists) setTempMappings(prev => [...prev, { processId: selectedProcessId, causeId }]);
  };

  const handleTempRemoveCause = (causeId) => {
    setTempMappings(prev => prev.filter(m => !(m.processId === selectedProcessId && m.causeId === causeId)));
  };

  const handleApplyModal = () => {
    setMappings(tempMappings);
    setShowAddModal(false);
  };

  const handleRemoveCause = (causeId) => {
    setMappings(prev => prev.filter(m => !(m.processId === selectedProcessId && m.causeId === causeId)));
  };

  const getCauseCount = (processId) => mappings.filter(m => m.processId === processId).length;

  const isCheckedInMatrix = (processId, causeId) => mappings.some(m => m.processId === processId && m.causeId === causeId);

  const handleSaveMaster = async () => {
    try {
      await saveSafetyMasterMappings({ mappings }, token, comCode);
      alert('마스터 매트릭스 설정이 저장되었습니다.');
    } catch (error) {
      console.error('저장 실패:', error);
      alert('저장에 실패했습니다.');
    }
  };

  return (
    <div className="sf-animate">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div style={{ fontSize: '0.95rem', color: '#334155', fontWeight: 500 }}>
          <i className="bi bi-info-circle-fill me-1" style={{ color: 'var(--sf-primary)' }}></i>
          새로운 프로젝트 생성 시 적용될 <strong>전사 표준 위험요인 맵핑</strong>을 설정합니다.
        </div>
        <div className="d-flex gap-2">
          <button className="sf-btn sf-btn-outline sf-btn-sm" onClick={() => setShowMatrixModal(true)}>
            <i className="bi bi-grid-3x3 me-1"></i> 전체 매트릭스 조감도
          </button>
          <button className="sf-btn sf-btn-primary sf-btn-sm" onClick={handleSaveMaster}>
            <i className="bi bi-save me-1"></i> 마스터 저장
          </button>
        </div>
      </div>

      <div className="row g-4">
        {/* ────── 좌측: 공정 리스트 ────── */}
        <div className="col-lg-3">
          <div className="sf-card h-100">
            <div className="sf-card-header sf-gradient-primary" style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', height: '53px' }}>
              <h5 className="sf-card-header-title" style={{ fontSize: '0.9rem', margin: 0 }}>평가 대상 공정 (표준)</h5>
            </div>
            <div className="sf-card-body" style={{ padding: 0 }}>
              {processes.length === 0 ? (
                <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                  <i className="bi bi-folder-x" style={{ fontSize: '2.5rem', color: '#cbd5e1' }}></i>
                  <div style={{ marginTop: 16, fontSize: '0.85rem', color: '#64748b', fontWeight: 600, wordBreak: 'keep-all' }}>
                    등록된 공정 마스터 데이터가 없습니다.<br />[공정 관리] 메뉴에서 먼저 공정을 등록해 주세요.
                  </div>
                </div>
              ) : (
                processes.map(proc => {
                  const isActive = selectedProcessId === proc.id;
                  const count = getCauseCount(proc.id);
                  return (
                    <div
                      key={proc.id}
                      onClick={() => setSelectedProcessId(proc.id)}
                      style={{
                        padding: '14px 16px', cursor: 'pointer', borderBottom: '1px solid var(--sf-border)',
                        background: isActive ? 'rgba(99,102,241,0.08)' : '#fff',
                        borderLeft: isActive ? '4px solid var(--sf-primary)' : '4px solid transparent',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: isActive ? 'var(--sf-primary)' : '#1e293b', wordBreak: 'keep-all', lineHeight: '1.4' }}>
                          {proc.name}
                        </div>
                        <span style={{
                          flexShrink: 0, whiteSpace: 'nowrap',
                          fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 12,
                          background: count > 0 ? '#e0e7ff' : '#fee2e2', color: count > 0 ? '#4338ca' : '#dc2626'
                        }}>{count > 0 ? `${count}건` : '0건'}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* ────── 우측: 공정별 유해·위험요인 매핑 ────── */}
        <div className="col-lg-9">
          <div className="sf-card h-100" style={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            {!selectedProcess ? (
              <div className="sf-card-body d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                  <i className="bi bi-arrow-left-circle" style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block' }}></i>
                  <h5 style={{ fontWeight: 700 }}>좌측에서 공정을 선택해 주세요</h5>
                </div>
              </div>
            ) : (
              <>
                <div className="sf-card-header sf-gradient-accent" style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '53px', height: 'auto', gap: '12px' }}>
                  <h5 className="sf-card-header-title" style={{ fontSize: '0.9rem', margin: 0, flex: 1, wordBreak: 'break-all' }}>
                    <i className="bi bi-exclamation-triangle me-2"></i>"{selectedProcess?.name}"의 유해·위험요인
                  </h5>
                  <button className="sf-btn sf-btn-white sf-btn-sm" style={{ fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }} onClick={handleOpenAddModal}>
                    <i className="bi bi-gear-fill me-1"></i> 위험원인 설정
                  </button>
                </div>
                <div className="sf-card-body">
                  <div>
                    {categories.map(cat => {
                      const catCauses = selectedCauses.filter(c => c.categoryId === cat.id);
                      return (
                        <div key={cat.id} style={{ marginBottom: 18 }}>
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, padding: '7px 12px',
                            borderRadius: 8, background: `${cat.color}12`, borderLeft: `4px solid ${cat.color}`,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                          }}>
                            <span style={{ fontWeight: 900, fontSize: '0.88rem', color: '#1e293b' }}>{cat.name}</span>
                            <span style={{
                              fontSize: '0.7rem', color: '#fff', background: cat.color, borderRadius: 10,
                              padding: '2px 8px', fontWeight: 700, lineHeight: '17px'
                            }}>{catCauses.length}건</span>
                          </div>
                          {catCauses.length === 0 ? (
                            <div style={{ padding: '10px', textAlign: 'center', color: '#94a3b8', fontSize: '0.78rem', background: '#fff', borderRadius: 8, border: '1px dashed #cbd5e1', marginLeft: 20 }}>
                              - 해당 공정에 매핑된 유해·위험요인이 없습니다 -
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 4px 4px 20px' }}>
                              {catCauses.map(cause => (
                                <div key={cause.id} style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px 4px 7px', borderRadius: 20,
                                  background: `${cat.subColor}18`, fontSize: '0.82rem', fontWeight: 600, color: cat.color,
                                  border: `1px solid ${cat.subColor}40`, cursor: 'default', transition: 'all 0.15s'
                                }}>
                                  <span style={{
                                    display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                                    background: cat.color, flexShrink: 0
                                  }}></span>
                                  <span style={{ color: '#334155', fontWeight: 500 }}>{cause.name}</span>
                                  <button onClick={() => handleRemoveCause(cause.id)} style={{
                                    border: 'none', background: 'transparent', cursor: 'pointer', color: '#cbd5e1',
                                    padding: 0, marginLeft: 2, fontSize: '0.75rem', lineHeight: 1, transition: 'color 0.15s'
                                  }} title="제거" onMouseOver={e => e.currentTarget.style.color='#ef4444'} onMouseOut={e => e.currentTarget.style.color='#cbd5e1'}>
                                    <i className="bi bi-x-circle-fill"></i>
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ────── 위험요인 추가 모달 (아코디언) ────── */}
      {showAddModal && createPortal(
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          <div style={{
            background: '#fff', width: '600px', maxHeight: '85vh', borderRadius: 16, display: 'flex', flexDirection: 'column',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', overflow: 'hidden'
          }}>
            <div style={{ padding: '20px 24px', background: 'var(--sf-gradient-primary)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', marginBottom: 4 }}>{selectedProcess?.name}</div>
                <h5 style={{ color: '#fff', fontWeight: 800, margin: 0 }}>위험요인 선택 (마스터 불러오기)</h5>
              </div>
              <button onClick={() => setShowAddModal(false)} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '2rem', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
            </div>

            <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
              {categories.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8' }}>
                  등록된 대분류가 없습니다.
                </div>
              ) : (
                categories.map(cat => {
                  const isExpanded = expandedCatId === cat.id;
                  const catCauses = causes.filter(c => c.categoryId === cat.id);
                  return (
                    <div key={cat.id} style={{ marginBottom: 12, border: `1px solid ${isExpanded ? cat.color : 'var(--sf-border)'}`, borderRadius: 12, overflow: 'hidden', transition: 'all 0.2s' }}>
                      <div 
                        onClick={() => setExpandedCatId(isExpanded ? null : cat.id)}
                        style={{ padding: '14px 20px', background: isExpanded ? `${cat.color}08` : '#f8fafc', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: cat.color }}></span>
                          <span style={{ fontWeight: 800, fontSize: '0.9rem', color: '#1e293b' }}>{cat.name}</span>
                        </div>
                      </div>
                      {isExpanded && (
                        <div style={{ padding: '8px 12px', background: '#fff' }}>
                          {catCauses.map(cause => {
                            const alreadyAdded = tempMappings.some(m => m.processId === selectedProcessId && m.causeId === cause.id);
                            return (
                              <div key={cause.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', fontSize: '0.85rem', borderBottom: '1px dashed #e2e8f0', color: '#475569' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                  <span style={{ fontWeight: 800, color: 'var(--sf-primary)', fontSize: '0.75rem' }}>{cause.code}</span>
                                  <span>{cause.name}</span>
                                </div>
                                {alreadyAdded ? (
                                  <button onClick={() => handleTempRemoveCause(cause.id)} style={{ border: 'none', background: '#dcfce7', color: '#166534', fontSize: '0.75rem', fontWeight: 700, padding: '4px 12px', borderRadius: 12, cursor: 'pointer', flexShrink: 0 }}>✓ 추가됨</button>
                                ) : (
                                  <button onClick={() => handleTempAddCause(cause.id)} style={{ border: '1px solid var(--sf-primary)', background: '#fff', color: 'var(--sf-primary)', fontSize: '0.75rem', fontWeight: 700, padding: '3px 12px', borderRadius: 12, cursor: 'pointer', flexShrink: 0 }}>+ 추가</button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', textAlign: 'right', background: '#f8fafc' }}>
              <button className="sf-btn sf-btn-primary" onClick={handleApplyModal}>선택 완료</button>
            </div>
          </div>
        </div>, document.body
      )}


      <SafetyMatrixModal
        show={showMatrixModal}
        onClose={() => setShowMatrixModal(false)}
        categories={categories}
        causes={causes}
        processes={processes}
        isCheckedInMatrix={isCheckedInMatrix}
      />
    </div>
  );
}

export default SafetyMasterHazardMap;
